import React, { useState } from 'react';
import { observer } from "mobx-react";
import { useHistory, useParams, useLocation } from "react-router-dom";
import Container from 'react-bootstrap/Container'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Tabs from 'react-bootstrap/Tabs'
import Tab from 'react-bootstrap/Tab'
import Breadcrumb from 'react-bootstrap/Breadcrumb'

import FourOhFourPage from './FourOhFourPage'

import { nameFromUrl } from '../lib/strFormatting'
import { getQueryParam } from '../lib/urlUtils'

import Disclaimer from '../components/Disclaimer';
import AirQualityPageHeader from '../components/AirQualityPageHeader';
import SelectLocation from '../components/SelectLocation';
import CountyReportTable from '../components/CountyReportTable';
import RegionSummary from '../components/AirQualityPage/RegionSummary';
import RegionAreaInfo from '../components/AirQualityPage/RegionAreaInfo';
import SelectRegion from '../components/SelectRegion';
import FireAndSmokeMap from '../components/FireAndSmokeMap';
import Link from '../components/Link'

import spinnerImg from '../images/dotdotdot.gif'

import {
    validateRegionStore,
    regionReportsTabDataStore,
    regionCountyListStore,
    regionsStore,
    regionCountyDataStore,
    regionAqiPercentagesStore,
    regionCovidDataStore,
    hpStateDataStore,
    countyTableDataStore,
    countyDailyPM25Store,
    countyCovidDataStore,
    stateGeojsonStore,
    stateCovidIntervalDataStore,
    stateAqiPercentagesStore,
    stateDailyPM25Store,
    goes16Store,
    stateCovidDataStore,
    stateStaticDataStore,
    stateDailySmokeStore,
    regionDailySmokeStore,
    countyGeojsonStore,
    countyRecentPM25Store,
    countyMonitorProximityStore,
    countyCovidIntervalDataStore,
    countyAqiPercentagesStore,
    countyStaticDataStore,
    countyFireDataStore,
    countyModelProjectionsStore,
    monitorDailyPM25Store
} from '../store/apiStores'

import './AirQualityPage.css'

// TODO: implement API to return country report data for specific
//  region, and delete filtering code in fetch callback
//const LATEST_REPORT_API_ENDPONT = '/api/county-report/latest';


export default function AirQualityPage() {
    let { region, state, county } = useParams();

    console.log(`Rendering AirQualityPage - ${region}`)

    // Stores that only need to be fetched once
    validateRegionStore.fetch({region: region, state: state, county: county})
    regionCountyListStore.fetch({region: region})
    regionCountyDataStore.fetch({region: region})
    regionsStore.fetch()

    return (
        <ObservedAirQualityPage region={region} state={state} county={county} />
    )
}

let ObservedAirQualityPage = observer((props) => {
    let regionPretty = nameFromUrl(props.region)

    const [mapIsVisible, setMapIsVisible] = useState(false);
    let handleTabSelect = (eventKey, event) => {
        if (eventKey=='latestMap' && !mapIsVisible) {
            event.preventDefault()
            window.open('http://fire.airnow.gov');
            // HACK: throw an exception to prevent opening of 'Latest Map' tab
            throw("stop navigation")
        }
    }

    if (!validateRegionStore.data) {
        return (
            <div className="fallback-spinner">
              <img src={spinnerImg} width="100" alt="Loading..." />
            </div>
        )
    } else if (!validateRegionStore.data.exists) {
        return (
            <FourOhFourPage />
        )
    } else {
        return (
            <div>
                <AirQualityPageHeader />
                <Container className="region-page" fluid={true}>
                    <AirQualityPageBreadCrumbs region={props.region}
                        state={props.state} county={props.county} />

                    <div className="d-lg-none">
                        <SelectRegion
                            region={regionPretty}
                            header="Change Region" small={true} />
                    </div>


                    <h1 style={{padding: "10px 0 10px"}}>{regionPretty} Region</h1>

                    <Disclaimer aboutLink="/tracking/air-quality/about/" aqOnly={true}/>

                    <Row style={{marginBottom: '20px'}}>
                        <Col>
                            <Tabs onSelect={handleTabSelect} defaultActiveKey="reports">
                                <Tab eventKey="reports" title="Reports">
                                    <AirQualityPageReportsTab region={props.region}
                                        state={props.state} county={props.county} />
                                </Tab>
                                <Tab eventKey="table" title="Regional Table">
                                    <div style={{height: "100%", "paddingTop": "50px"}}>
                                        <CountyReportTable
                                            pageSize={10}
                                            showAll={getQueryParam('table') == 'all'}
                                            aqOnly={true}
                                            includeFilter={true}
                                            includePagination={true}
                                            useRegionCountyDataStore={true}
                                            noStateCountyLinks={true} />
                                    </div>
                                </Tab>
                                <Tab eventKey="latestMap" title="Latest Map">
                                    <FireAndSmokeMap isVisible={mapIsVisible} aqOnly={true} />
                                </Tab>
                            </Tabs>
                        </Col>
                    </Row>
                </Container>
            </div>
        )
    }
})

function AirQualityPageBreadCrumbs(props) {
    let regionPretty = nameFromUrl(props.region)
    let statePretty = nameFromUrl(props.state)
    let countyPretty = nameFromUrl(props.county)

    if (countyPretty) {
        return (
            <Breadcrumb>
                <Breadcrumb.Item href={'/tracking/air-quality/'}>Home</Breadcrumb.Item>
                <Breadcrumb.Item href={`/tracking/air-quality/${props.region}/`}>{regionPretty}</Breadcrumb.Item>
                <Breadcrumb.Item href={`/tracking/air-quality/${props.region}/${props.state}/`}>{statePretty}</Breadcrumb.Item>
                <Breadcrumb.Item active>{countyPretty}</Breadcrumb.Item>
            </Breadcrumb>
        )
    } else if (statePretty) {
        return (
            <Breadcrumb>
                <Breadcrumb.Item href={'/tracking/air-quality/'}>Home</Breadcrumb.Item>
                <Breadcrumb.Item href={`/tracking/air-quality/${props.region}/`}>{regionPretty}</Breadcrumb.Item>
                <Breadcrumb.Item active>{statePretty}</Breadcrumb.Item>
            </Breadcrumb>
        )
    } else {
        return (
            <Breadcrumb>
                <Breadcrumb.Item href={'/tracking/air-quality/'}>Home</Breadcrumb.Item>
                <Breadcrumb.Item active>{regionPretty}</Breadcrumb.Item>
            </Breadcrumb>
        )
    }
}

function AirQualityPageReportsTab(props) {
    console.log("Rendering AirQualityPageReportsTab")

    // let location = useLocation();
    // React.useEffect(() => {
    //     console.log("AirQualityPageReportsTab - location changed, setting state and county to null")
    //     setStateCounty({state: null, county: null})
    // }, [location]);

    if (props.county) {
        console.log(`AirQualityPageReportsTab - Loading data for ${props.county}, ${props.state}`)
        regionReportsTabDataStore.fetch({state: props.state, county: props.county});
        countyTableDataStore.fetch({state: props.state, county: props.county});
        stateStaticDataStore.fetch({state: props.state});
        countyGeojsonStore.fetch({state: props.state, county: props.county});
        countyRecentPM25Store.fetch({state: props.state, county: props.county});
        countyDailyPM25Store.fetch({state: props.state, county: props.county});
        countyCovidDataStore.fetch({state: props.state, county: props.county});
        countyMonitorProximityStore.fetch({state: props.state, county: props.county});
        countyCovidIntervalDataStore.fetch({state: props.state, county: props.county});
        countyAqiPercentagesStore.fetch({state: props.state, county: props.county});
        countyStaticDataStore.fetch({state: props.state, county: props.county});
        countyFireDataStore.fetch({state: props.state, county: props.county});
        countyModelProjectionsStore.fetch({state: props.state, county: props.county});
        goes16Store.fetch({state: props.state, county: props.county});
        monitorDailyPM25Store.fetch({state: props.state, county: props.county});
    } else if (props.state) {
        console.log(`AirQualityPageReportsTab - Loading data for ${props.state}`)
        regionReportsTabDataStore.fetch({state: props.state});
        hpStateDataStore.fetch();
        countyTableDataStore.fetch({state: props.state});
        stateGeojsonStore.fetch({state: props.state});
        stateCovidIntervalDataStore.fetch({state: props.state});
        stateAqiPercentagesStore.fetch({state: props.state});
        stateDailyPM25Store.fetch({state: props.state});
        goes16Store.fetch({state: props.state});
        stateCovidDataStore.fetch({state: props.state});
        stateStaticDataStore.fetch({state: props.state});
        stateDailySmokeStore.fetch({state: props.state});
    } else {
        console.log(`AirQualityPageReportsTab - Loading data for ${props.region} region`)
        regionReportsTabDataStore.fetch({region: props.region})
        regionCovidDataStore.fetch({region: props.region})
        regionAqiPercentagesStore.fetch({region: props.region})
        regionDailySmokeStore.fetch({region: props.region})
    }


    const history = useHistory();
    let changeLocationHandler = (state, county) => {
        if (state != props.state || county != props.county) {
            console.log(`AirQualityPageReportsTab - state and/or county`
                + ` change: ${props.county} - ${props.state}`)

            let newUrlPath = '/tracking/air-quality/' + props.region;
            if (state && state != "") {
                newUrlPath += '/' + state.replace(/\s+/g, '-').toLowerCase()
                if (county && county != "") {
                    newUrlPath += '/' + county.replace(/\s+/g, '-').toLowerCase()
                }
            }
            newUrlPath +=  (window.location.search || '')

            console.log(`Changing location to ${newUrlPath}`)
            history.push(newUrlPath);
        }
    }

    return (
        <div>
            <div style={{height: "100%", "paddingTop": "50px"}}>
                <SelectLocation
                    useRegionCountyStore={true}
                    changeLocationHandler={changeLocationHandler}
                    state={props.state}
                    county={props.county}
                    unselectedStateOption={'all states'}
                    unselectedCountyOption={'all counties'}
                    showCountiesSelectWhenEmpty={true}
                    changeLocationButtonText={'Show'} />
            </div>
            <div style={{height: "100%", "paddingTop": "50px"}}>
                <RegionSummary
                    region={nameFromUrl(props.region)}
                    state={nameFromUrl(props.state)}
                    county={nameFromUrl(props.county)} />
            </div>
        </div>
    )
}
